import { useEffect, useRef } from 'react';

/**
 * Hook to scroll a message into view when streaming starts.
 * Only active while animating. Cleans up immediately when animation is done.
 * 
 * This uses ResizeObserver/MutationObserver to keep bottom padding in sync as
 * message height changes, but intentionally does NOT keep forcing scroll position
 * after the initial snap so manual user scrolling is respected.
 * 
 * @param messageId - The ID of the message being animated (null when not animating)
 * @param containerRef - Reference to the scroll container
 * @param isAnimating - Whether animation is currently active
 */
export function useAnimatedMessageScroll(
  messageId: string | number | null,
  containerRef: React.RefObject<HTMLDivElement>,
  isAnimating: boolean
) {
  const hasSnappedRef = useRef<string | number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const minMessageHeightRef = useRef<number>(Infinity);
  const activeContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Only perform one-time snap while actively animating with valid refs
    if (!isAnimating || !messageId || !containerRef?.current) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      if (activeContainerRef.current) {
        // Restore CSS-managed padding after streaming completes
        activeContainerRef.current.style.paddingBottom = '';
      }
      return;
    }

    // containerRef can be either a direct DOM element or a handle with a .container property
    const container = (containerRef.current as any)?.container || (containerRef.current as HTMLDivElement);

    // Safety check: container must be a valid DOM element
    if (!container || typeof container.querySelector !== 'function') {
      return;
    }
    activeContainerRef.current = container;

    const messageEl = container.querySelector(
      `[data-message-id="${messageId}"]`
    ) as HTMLElement | null;

    if (!messageEl) return;

    // Scroll to message position
    const scrollToMessage = () => {
      const elementTop = messageEl.offsetTop - container.offsetTop;
      const scrollTarget = Math.max(0, elementTop - 100);
      container.scrollTop = scrollTarget;
    };

    const updatePadding = () => {
      const currentHeight = messageEl.offsetHeight;
      const viewportHeight = container.clientHeight;

      minMessageHeightRef.current = Math.min(minMessageHeightRef.current, currentHeight);
      const growthAmount = currentHeight - minMessageHeightRef.current;

      if (growthAmount > 0) {
        const newPadding = Math.max(0, viewportHeight - currentHeight);
        container.style.paddingBottom = `${newPadding}px`;
      } else {
        const initialPadding = Math.max(0, viewportHeight - currentHeight);
        container.style.paddingBottom = `${initialPadding}px`;
      }
    };

    if (hasSnappedRef.current !== messageId) {
      hasSnappedRef.current = messageId;
      minMessageHeightRef.current = Infinity;
      requestAnimationFrame(() => {
        scrollToMessage();
        updatePadding();
      });
    } else {
      updatePadding();
    }

    observerRef.current = new ResizeObserver(() => {
      updatePadding();
    });
    observerRef.current.observe(messageEl);

    mutationObserverRef.current = new MutationObserver(() => {
      updatePadding();
    });
    mutationObserverRef.current.observe(messageEl, {
      characterData: true,
      subtree: true,
      childList: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
    };
  }, [messageId, isAnimating, containerRef]);
}
